import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Observable } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { of } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "../../shared/toastr.service";
import {
  GET_BANNERS,
  GetBannersSuccessAction,
  GetBannersFailureAction,
  CHANGE_BANNER,
  ChangeBannerAction,
  ChangeBannerSuccessAction,
  ChangeBannerFailureAction,
  REMOVE_BANNER,
  RemoveBannerAction,
  RemoveBannerSuccessAction,
  RemoveBannerFailureAction,
  ADD_BANNER,
  ADD_BANNER_FAILURE,
  AddBannerAction,
  AddBannerSuccessAction,
  AddBannerFailureAction,
} from "./banners.actions";
import { BannersService } from "./banners.service";
import {
  AddBannerObject,
  Banner,
  Banners,
  ChangeBannerObject,
} from "./banners.interface";
import { MatDialog } from "@angular/material/dialog";
import { LimitPlanDialogComponent } from "../../shared/limit-plan-dialog/limit-plan-dialog.component";

@Injectable()
export class BannersEffects {
  getBanners$: Observable<unknown> = createEffect(() => {
    return this.actions$.pipe(
      ofType(GET_BANNERS),
      switchMap(() => {
        return this.bannersService.getBanners().pipe(
          switchMap((response: Banners) => {
            return [new GetBannersSuccessAction(response)];
          }),
          catchError((error) => of(new GetBannersFailureAction(error))),
        );
      }),
    );
  });

  changeBanner$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(CHANGE_BANNER),
      map((action: ChangeBannerAction) => action.payload),
      switchMap((payload: ChangeBannerObject) => {
        return this.bannersService.changeBanner(payload).pipe(
          switchMap((response: Banner) => {
            this.toastr.success(
              this.translate.instant("addons.banners.editSuccess") as string,
            );
            return [new ChangeBannerSuccessAction(response)];
          }),
          catchError((error) => of(new ChangeBannerFailureAction(error))),
        );
      }),
    ),
  );

  removeBanner$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(REMOVE_BANNER),
      map((action: RemoveBannerAction) => action.payload),
      switchMap((payload: string) => {
        return this.bannersService.removeBanner(payload).pipe(
          switchMap(() => {
            this.toastr.success(
              this.translate.instant("addons.banners.deleteSuccess") as string,
            );
            return [new RemoveBannerSuccessAction(payload)];
          }),
          catchError((error) => of(new RemoveBannerFailureAction(error))),
        );
      }),
    ),
  );

  addBanner$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(ADD_BANNER),
      map((action: AddBannerAction) => action.payload),
      switchMap((payload: AddBannerObject) => {
        return this.bannersService.addBanner(payload).pipe(
          switchMap((response) => {
            this.toastr.success(
              this.translate.instant("addons.banners.addSuccess") as string,
            );
            return [new AddBannerSuccessAction(response)];
          }),
          catchError((error) => of(new AddBannerFailureAction(error))),
        );
      }),
    ),
  );

  addBannerFailure$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ADD_BANNER_FAILURE),
        tap((action: AddBannerFailureAction) => {
          const payload = action.payload as
            | { error?: { message?: string }; message?: string }
            | { error?: string }
            | string
            | undefined;
          const errorMessage =
            (typeof payload === "object" &&
              payload !== null &&
              ("error" in payload
                ? typeof payload.error === "object" && payload.error !== null
                  ? "message" in payload.error
                    ? payload.error.message
                    : undefined
                  : typeof payload.error === "string"
                    ? payload.error
                    : undefined
                : "message" in payload
                  ? payload.message
                  : undefined)) ||
            (typeof payload === "string" ? payload : "") ||
            "";
          if (
            typeof errorMessage === "string" &&
            errorMessage.includes("BANNER_LIMIT_REACHED")
          ) {
            this.dialog.open(LimitPlanDialogComponent, {
              width: "500px",
              maxWidth: "90vw",
              data: { plan: "basic", type: "banner" },
            });
          } else {
            this.toastr.error(
              this.translate.instant("addons.banners.addError") as string,
            );
          }
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private bannersService: BannersService,
    private translate: TranslateService,
    private toastr: ToastrService,
    private dialog: MatDialog,
  ) {}
}
